import { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClaimStateBadges } from '@/components/Students/ClaimStateBadges';
import { StatusIndicator } from '@/components/StatusIndicator';
import { Section } from '@/components/Section';
import Button from '@/components/core/Button';
import {
  COLLECTION_TYPE_LABELS,
  STUDENT_TYPE_VIEW,
} from '@/helpers/collectionTypes';
import { getMembershipStatus } from '@/helpers/membershipStatus';
import { useStudentClaimErrors } from '@/hooks/useStudentClaimErrors';
import { useTenantID } from '@/hooks/useTenantID';

export const DashboardStudentClaimErrors = () => {
  const titleId = useId();
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const { errors, isLoading } = useStudentClaimErrors();

  if (isLoading || errors.length === 0) return null;

  return (
    <Section aria-labelledby={titleId} role="region" className="gap-4">
      <div className="flex flex-col gap-1">
        <h2 id={titleId} className="w-full">
          <FormattedMessage
            defaultMessage="{count, plural, one {# elev med fel} other {# elever med fel}}"
            values={{ count: errors.length }}
          />
        </h2>
        <p className="text-text-800">
          <FormattedMessage defaultMessage="Biljetthanteringen har inte kunnat slutföras för dessa elever." />
        </p>
      </div>
      <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
        {errors.map((student) => {
          const name = `${student.firstName} ${student.lastName}`.trim();
          const category = formatMessage(
            COLLECTION_TYPE_LABELS[student.collectionType],
          );

          return (
            <li key={`${student.collectionType}-${student.collectionMemberId}`}>
              <Button
                to="/$tenantId/travellers/$travellerId/$view"
                params={{
                  tenantId,
                  travellerId: student.travellerId,
                  view: STUDENT_TYPE_VIEW[student.studentType],
                }}
                className="h-auto w-full flex-col items-start gap-1 py-2 text-left font-normal"
              >
                <span className="flex w-full flex-wrap items-center gap-2">
                  <StatusIndicator
                    status={getMembershipStatus({
                      active: student.active,
                      inactivate_at: student.inactivateAt,
                    })}
                    inactivateAt={student.inactivateAt}
                  />
                  {student.nationalIdentityNumber && (
                    <span className="text-nowrap font-mono text-text-800">
                      {student.nationalIdentityNumber}
                    </span>
                  )}
                  <span className="truncate">{name}</span>
                  <span className="ml-auto truncate text-text-800">
                    {category}
                  </span>
                </span>
                <ClaimStateBadges
                  claimStates={student.states}
                  className="w-full"
                />
              </Button>
            </li>
          );
        })}
      </ul>
    </Section>
  );
};
